import type { AdminRole } from "@/lib/schema";
import {
  Activity,
  AlertCircle,
  BarChart3,
  BookOpen,
  Clock,
  CreditCard,
  DollarSign,
  FileText,
  Heart,
  TrendingUp,
  UserCheck,
  UserPlus,
  Users,
} from "lucide-react";

export type DashboardSection =
  | "metric_cards"
  | "engagement_charts"
  | "care_queue"
  | "subscription_health"
  | "content_overview"
  | "content_studio_overview"
  | "coach_overview"
  | "recent_activity"
  | "pending_reviews"
  | "content_performance"
  | "content_gaps"
  | "coach_submissions"
  | "coach_activity"
  | "coach_quality"
  | "my_content"
  | "my_profile"
  | "revenue_chart";

export interface MetricCardConfig {
  id: string;
  title: string;
  dataKey: string;
  trendKey?: string;
  icon: React.ElementType;
  iconBg: string;
  iconColor: string;
  format?: "number" | "percent" | "currency";
  suffix?: string;
}

export interface RoleConfig {
  role: AdminRole;
  title: string;
  subtitle: string;
  heroMessage: string;
  metricCards: MetricCardConfig[];
  sections: DashboardSection[];
  primaryFocus: string;
  canViewSubscriptions: boolean;
  canViewRevenue: boolean;
}

export const roleConfigs: Record<AdminRole, RoleConfig> = {
  super_admin: {
    role: "super_admin",
    title: "Super Admin Dashboard",
    subtitle: "Ecosystem Overview",
    heroMessage:
      "Full visibility across users, content, coaches, and business health",
    primaryFocus: "Strategic oversight of the entire platform",
    canViewSubscriptions: true,
    canViewRevenue: true,
    metricCards: [
      {
        id: "active_users",
        title: "Active Users",
        dataKey: "totalActiveUsers",
        trendKey: "totalActiveUsersTrend",
        icon: Users,
        iconBg: "bg-violet-100 dark:bg-violet-900/30",
        iconColor: "text-violet-600 dark:text-violet-400",
        format: "number",
      },
      {
        id: "engagement",
        title: "Engagement Rate",
        dataKey: "averageEngagement",
        trendKey: "averageEngagementTrend",
        icon: Activity,
        iconBg: "bg-rose-100 dark:bg-rose-900/30",
        iconColor: "text-rose-600 dark:text-rose-400",
        format: "percent",
      },
      {
        id: "subscriptions",
        title: "Active Subscriptions",
        dataKey: "activeMemberships",
        trendKey: "activeMembershipsTrend",
        icon: CreditCard,
        iconBg: "bg-emerald-100 dark:bg-emerald-900/30",
        iconColor: "text-emerald-600 dark:text-emerald-400",
        format: "number",
      },
      {
        id: "revenue",
        title: "Monthly Revenue",
        dataKey: "monthlySupportValue",
        trendKey: "monthlySupportValueTrend",
        icon: DollarSign,
        iconBg: "bg-teal-100 dark:bg-teal-900/30",
        iconColor: "text-teal-600 dark:text-teal-400",
        format: "currency",
      },
    ],
    sections: [
      "metric_cards",
      "content_studio_overview",
      "revenue_chart",
      "engagement_charts",
      "care_queue",
      "subscription_health",
      "recent_activity",
    ],
  },

  admin: {
    role: "admin",
    title: "Admin Dashboard",
    subtitle: "Operational Control",
    heroMessage:
      "Monitor user health, manage risks, and ensure smooth operations",
    primaryFocus: "Day-to-day operational oversight",
    canViewSubscriptions: true,
    canViewRevenue: true,
    metricCards: [
      {
        id: "active_users",
        title: "Active Users",
        dataKey: "totalActiveUsers",
        trendKey: "totalActiveUsersTrend",
        icon: Users,
        iconBg: "bg-violet-100 dark:bg-violet-900/30",
        iconColor: "text-violet-600 dark:text-violet-400",
        format: "number",
      },
      {
        id: "trials_ending",
        title: "Trials Ending Soon",
        dataKey: "trialsEnding",
        icon: Clock,
        iconBg: "bg-amber-100 dark:bg-amber-900/30",
        iconColor: "text-amber-600 dark:text-amber-400",
        format: "number",
      },
      {
        id: "at_risk",
        title: "At-Risk Members",
        dataKey: "membersNeedingSupport",
        icon: Heart,
        iconBg: "bg-rose-100 dark:bg-rose-900/30",
        iconColor: "text-rose-600 dark:text-rose-400",
        format: "number",
      },
    ],
    sections: [
      "metric_cards",
      "content_studio_overview",
      "care_queue",
      "pending_reviews",
      "engagement_charts",
      "subscription_health",
      "recent_activity",
    ],
  },

  coach: {
    role: "coach",
    title: "Coach Dashboard",
    subtitle: "My Content & Profile",
    heroMessage:
      "Create wellness content, view your profile, and track your submissions",
    primaryFocus: "Content creation and profile management",
    canViewSubscriptions: false,
    canViewRevenue: false,
    metricCards: [
      {
        id: "my_drafts",
        title: "My Drafts",
        dataKey: "myDrafts",
        icon: FileText,
        iconBg: "bg-amber-100 dark:bg-amber-900/30",
        iconColor: "text-amber-600 dark:text-amber-400",
        format: "number",
      },
      {
        id: "my_published",
        title: "My Published",
        dataKey: "myPublished",
        icon: BookOpen,
        iconBg: "bg-emerald-100 dark:bg-emerald-900/30",
        iconColor: "text-emerald-600 dark:text-emerald-400",
        format: "number",
      },
      {
        id: "pending_approval",
        title: "Pending Approval",
        dataKey: "pendingApproval",
        icon: Clock,
        iconBg: "bg-violet-100 dark:bg-violet-900/30",
        iconColor: "text-violet-600 dark:text-violet-400",
        format: "number",
      },
      {
        id: "total_views",
        title: "Total Views",
        dataKey: "totalViews",
        icon: Activity,
        iconBg: "bg-blue-100 dark:bg-blue-900/30",
        iconColor: "text-blue-600 dark:text-blue-400",
        format: "number",
      },
    ],
    sections: ["metric_cards", "my_content", "my_profile", "recent_activity"],
  },

  coach_manager: {
    role: "coach_manager",
    title: "Coach Success",
    subtitle: "Coach Management",
    heroMessage:
      "Support coaches, track performance, and grow the creator community",
    primaryFocus: "Coach success and quality",
    canViewSubscriptions: false,
    canViewRevenue: false,
    metricCards: [
      {
        id: "active_coaches",
        title: "Active Coaches",
        dataKey: "activeCoaches",
        icon: UserCheck,
        iconBg: "bg-emerald-100 dark:bg-emerald-900/30",
        iconColor: "text-emerald-600 dark:text-emerald-400",
        format: "number",
      },
      {
        id: "onboarding",
        title: "Onboarding",
        dataKey: "onboardingCoaches",
        icon: UserPlus,
        iconBg: "bg-blue-100 dark:bg-blue-900/30",
        iconColor: "text-blue-600 dark:text-blue-400",
        format: "number",
      },
      {
        id: "submissions",
        title: "Pending Submissions",
        dataKey: "pendingCoachSubmissions",
        icon: FileText,
        iconBg: "bg-amber-100 dark:bg-amber-900/30",
        iconColor: "text-amber-600 dark:text-amber-400",
        format: "number",
      },
      {
        id: "needs_attention",
        title: "Needs Attention",
        dataKey: "coachesNeedingAttention",
        icon: Heart,
        iconBg: "bg-rose-100 dark:bg-rose-900/30",
        iconColor: "text-rose-600 dark:text-rose-400",
        format: "number",
      },
    ],
    sections: [
      "metric_cards",
      "coach_activity",
      "coach_quality",
      "recent_activity",
    ],
  },

  influencer: {
    role: "influencer",
    title: "Influencer Dashboard",
    subtitle: "Audience & Referrals",
    heroMessage: "Track your followers, referrals, and commission earned",
    primaryFocus: "Audience growth and affiliate referrals",
    canViewSubscriptions: false,
    canViewRevenue: false,
    metricCards: [
      {
        id: "followers",
        title: "Followers",
        dataKey: "followers",
        icon: Users,
        iconBg: "bg-violet-100 dark:bg-violet-900/30",
        iconColor: "text-violet-600 dark:text-violet-400",
        format: "number",
      },
      {
        id: "referral_signups",
        title: "Referral Signups",
        dataKey: "referralSignups",
        icon: UserPlus,
        iconBg: "bg-amber-100 dark:bg-amber-900/30",
        iconColor: "text-amber-600 dark:text-amber-400",
        format: "number",
      },
      {
        id: "paid_referrals",
        title: "Paid Referrals",
        dataKey: "paidReferrals",
        icon: CreditCard,
        iconBg: "bg-rose-100 dark:bg-rose-900/30",
        iconColor: "text-rose-600 dark:text-rose-400",
        format: "number",
      },
      {
        id: "commission_earned",
        title: "Commission Earned",
        dataKey: "commissionEarned",
        icon: DollarSign,
        iconBg: "bg-emerald-100 dark:bg-emerald-900/30",
        iconColor: "text-emerald-600 dark:text-emerald-400",
        format: "number",
      },
    ],
    // Only sections backed by influencer data (EVO-48).
    sections: ["metric_cards"],
  },

  content_manager: {
    role: "content_manager",
    title: "Content Manager Dashboard",
    subtitle: "Content Strategy",
    heroMessage:
      "Oversee content creation, monitor quality, and ensure content strategy alignment",
    primaryFocus: "Content strategy and quality management",
    canViewSubscriptions: false,
    canViewRevenue: false,
    metricCards: [
      {
        id: "content_reviewed",
        title: "Content Reviewed",
        dataKey: "contentReviewed",
        icon: FileText,
        iconBg: "bg-amber-100 dark:bg-amber-900/30",
        iconColor: "text-amber-600 dark:text-amber-400",
        format: "number",
      },
      {
        id: "content_approved",
        title: "Content Approved",
        dataKey: "contentApproved",
        icon: UserCheck,
        iconBg: "bg-emerald-100 dark:bg-emerald-900/30",
        iconColor: "text-emerald-600 dark:text-emerald-400",
        format: "number",
      },
      {
        id: "content_gaps",
        title: "Content Gaps",
        dataKey: "contentGaps",
        icon: AlertCircle,
        iconBg: "bg-rose-100 dark:bg-rose-900/30",
        iconColor: "text-rose-600 dark:text-rose-400",
        format: "number",
      },
      {
        id: "content_performance",
        title: "Content Performance",
        dataKey: "contentPerformance",
        icon: BarChart3,
        iconBg: "bg-blue-100 dark:bg-blue-900/30",
        iconColor: "text-blue-600 dark:text-blue-400",
        format: "percent",
      },
    ],
    sections: [
      "metric_cards",
      "content_overview",
      "content_gaps",
      "content_performance",
      "recent_activity",
    ],
  },
};

export function getRoleConfig(role: AdminRole): RoleConfig {
  return roleConfigs[role] || roleConfigs.admin;
}

export function canViewSection(
  role: AdminRole,
  section: DashboardSection,
): boolean {
  const config = getRoleConfig(role);
  return config.sections.includes(section);
}

export function getVisibleMetricCards(role: AdminRole): MetricCardConfig[] {
  return getRoleConfig(role).metricCards;
}
